/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import MobileFieldView from '@airasia-phoenix/react-native/components/MobileFieldView';
import { SelectionFieldRefs } from '@airasia-phoenix/react-native/components/SelectionFieldView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { SelectedCountry } from '../../utils/mapCountries';
import { withPlusDialingCode } from '../../utils/phoneUtils';
import { DialingCodeDropdownFieldView } from '../DialingCodeDropdownFieldView';
import {
  MobileLoginErrorsType,
  MobileLoginType,
} from '../../widgets/LoginView/types';
import styles from './styles';

export type MobileLoginFormViewProps = {
  mobileLogin: MobileLoginType;
  errors: MobileLoginErrorsType;
  mobileErrorMessage: string;
  mobileFieldRef: RefObject<SelectionFieldRefs | null>;
  countrySearch: string;
  setCountrySearch: (search: string) => void;
  countryData: { title: string; data: SelectedCountry[] }[];
  onMobileTextChange: (text: string) => void;
  onDialingCodeChange: (dialingCode: string) => void;
  mobileFieldDisabled?: boolean;
  dialingCodeFieldDisabled?: boolean;
};

export function MobileLoginFormView({
  mobileLogin,
  errors,
  mobileErrorMessage,
  mobileFieldRef,
  countrySearch,
  setCountrySearch,
  countryData,
  onMobileTextChange,
  onDialingCodeChange,
  mobileFieldDisabled = false,
  dialingCodeFieldDisabled = false,
}: MobileLoginFormViewProps) {
  const { t } = useTranslation('account');
  const isDesktopWeb = Platform.OS === 'web' && getCurrentBreakpoint() > 1;

  const handleCountrySelect = (selected: SelectedCountry[] = []) => {
    const selectedCountryCode = selected[0]?.model?.dialingCode;
    if (selectedCountryCode) {
      onDialingCodeChange(selectedCountryCode);
    }
    mobileFieldRef.current?.toggleBottomSheetView();
    setCountrySearch('');
  };

  const dialingCodeFieldViewProps = {
    label: t('loginPage.dialCodeLabel'),
    value: withPlusDialingCode(mobileLogin.dialingCode),
    disabled: dialingCodeFieldDisabled,
    endIcon: {
      icon: pd.icon.main.navigation.arrow_drop_down,
      height: 24,
      width: 24,
      tint: getColor(pd.sys.colour.content_secondary),
    },
    bottomSheetViewProps: {
      initialHeight: '90%',
      finalHeight: '90%',
      height: '100%' as const,
      showHandle: true,
      showHeader: true,
      sectionTitle: t('loginPage.selectCountry'),
      children: undefined,
    },
    searchableListViewProps: {
      textFieldViewProps: {
        label: t('loginPage.searchCountry'),
        value: countrySearch,
        onChangeText: setCountrySearch,
        disabled: false,
      },
      isInsideBottomSheetView: true,
      searchableListPageViewData: countryData,
      onChange: handleCountrySelect,
      isTickList: false,
    },
  };

  if (isDesktopWeb) {
    return (
      <View style={[styles.mobileFieldContainer, styles.desktopRow]}>
        <View style={styles.dialingCodeFieldStyle}>
          <DialingCodeDropdownFieldView
            testID="mobile-field-dialing-code"
            label={t('loginPage.dialCodeLabel')}
            dialingCode={mobileLogin.dialingCode}
            countrySearch={countrySearch}
            setCountrySearch={setCountrySearch}
            onDialingCodeChange={onDialingCodeChange}
            disabled={dialingCodeFieldDisabled}
          />
        </View>
        <View style={[styles.mobileNumberFieldStyle, styles.desktopFieldGap]}>
          <TextFieldView
            testID="mobile-field"
            label={t('loginPage.mobileNumberLabel')}
            value={mobileLogin.mobileNumber}
            onChangeText={(text) => onMobileTextChange(text.replace(/\D/g, ''))}
            keyboardType="number-pad"
            disabled={mobileFieldDisabled}
            isError={errors.mobileNumberError || !!mobileErrorMessage}
            errorMessage={mobileErrorMessage}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.mobileFieldContainer}>
      <MobileFieldView
        testID="mobile-field"
        ref={mobileFieldRef}
        value={mobileLogin.mobileNumber}
        onChangeText={onMobileTextChange}
        errorMessage={mobileErrorMessage}
        label={t('loginPage.mobileNumberLabel')}
        disabled={mobileFieldDisabled}
        textFieldViewFlextStyleProps={{
          dialingCode: styles.dialingCodeFieldStyle,
          mobileNumber: styles.mobileNumberFieldStyle,
        }}
        {...(errors.mobileNumberError ? { isError: true } : {})}
        dialingCodeFieldViewProps={dialingCodeFieldViewProps}
        key={`mobile-field-${t('loginPage.selectCountry')}`}
      />
    </View>
  );
}
